// Adapters: the LIVE pipeline -> the source-graph model.
//
// The navigator's data really is evidence-backed already - it just is not
// modeled. This module formalizes what each runtime fact actually proves:
//
//   runtime fact                              -> graph claim
//   --------------------------------------------------------------------------
//   Sefaria getLinks record                   -> Evidence "provider_link"
//   ref shape "X on Y" (commentaryOn bucket)  -> SourceEdge comments_on,
//                                                edgeClass structural, VERIFIED
//                                                (+ Evidence structural_commentary_link)
//   citations.ts dual gate (cited bucket)     -> SourceEdge cites, edgeClass
//                                                citation, HIGH confidence
//                                                (+ Evidence explicit_ref, parser
//                                                "citedInText")
//   otherConnected bucket                     -> SourceEdge cross_reference,
//                                                neutral + bidirectional (the
//                                                direction-honesty rule: never
//                                                assert a backwards citation)
//
// Everything is deterministic - ids derive from refs, so the same link always
// produces the same edge (inspectable, dedupable). No Date.now(), no fetch,
// no AI. Adapters never invent a ref: every ref passed in originated from a
// real Sefaria response upstream (accuracy-lock).

import { lookupWork, lookupCommentator, parseCanonicalRef, slugifyTitle, hebrewNumeral, structuralBaseMatches } from "./normalizeRef.ts";
import type {
  Evidence,
  JourneyNode,
  NormalizedRef,
  Passage,
  SourceEdge,
  Work,
} from "./sourceGraph.ts";
import type { PathNode } from "./journey.ts";

// The minimal structural shape of a navigator Item / grouped Sefaria link.
export interface LinkLike {
  ref: string;
  commentator: string;
  commentatorHe: string | null;
  linkType: string;
  category: string;
}

// Which honest bucket the navigator put a connection in (see loadCites).
export type ConnectionBucket = "cited" | "commentaryOn" | "otherConnected";

export const passageId = (ref: string): string => `p:${ref}`;
export const edgeId = (bucket: ConnectionBucket, fromRef: string, toRef: string): string =>
  `e:${bucket}:${fromRef} -> ${toRef}`;

export function sefariaUrl(ref: string): string {
  return `https://www.sefaria.org/${encodeURIComponent(ref.replace(/ /g, "_"))}`;
}

// ---------------------------------------------------------------------------
// Work + Passage construction from a canonical Sefaria ref
// ---------------------------------------------------------------------------

// Build the Work record implied by a canonical ref. Known works (normalizeRef
// table) get full metadata; unknown ones get an honest minimal record with the
// category Sefaria supplied (never a guessed one).
export function workFromRef(ref: string, sefariaCategory?: string): Work | null {
  const parsed = parseCanonicalRef(ref);
  if (!parsed) return null;
  const known = lookupWork(parsed.workTitle);
  const baseKnown = parsed.baseWorkTitle ? lookupWork(parsed.baseWorkTitle) : null;
  const isCommentary = parsed.baseWorkTitle !== null;
  const commentatorName = isCommentary ? parsed.workTitle.split(/ on /i)[0].trim() : null;
  const commentator = commentatorName ? lookupCommentator(commentatorName) : null;

  return {
    id: known?.id ?? slugifyTitle(parsed.workTitle),
    canonicalTitle: known?.en ?? parsed.workTitle,
    heTitle: known?.he || (commentator && baseKnown ? `${commentator.he} על ${baseKnown.he}` : undefined),
    category: known?.category ?? (isCommentary ? "Commentary" : mapSefariaCategory(sefariaCategory)),
    baseWorkId: baseKnown?.id ?? (parsed.baseWorkTitle ? slugifyTitle(parsed.baseWorkTitle) : undefined),
    // Only classify the commentary type when the BASE work is actually known -
    // guessing "commentary_on_tanach" for an unknown base would be invented.
    commentaryType:
      isCommentary && baseKnown
        ? baseKnown.addressing === "daf_amud"
          ? "commentary_on_talmud"
          : "commentary_on_tanach"
        : undefined,
    sourceProviderIds: [{ provider: "Sefaria", id: parsed.workTitle }],
    status: "active",
  };
}

// Sefaria top-level category -> our SourceCategory (only where the mapping is
// certain; anything else is honestly "Other").
function mapSefariaCategory(cat?: string): Work["category"] {
  switch ((cat ?? "").trim().toLowerCase()) {
    case "tanakh": return "Tanach";
    case "targum": return "Commentary";
    case "commentary": return "Commentary";
    case "mishnah": return "Mishnah";
    case "talmud": return "Talmud";
    case "tosefta": return "Talmud";
    case "midrash": return "Midrash";
    case "halakhah": return "Halacha";
    case "responsa": return "Responsa";
    case "kabbalah": return "Kabbalah";
    case "chasidut": return "Chassidut";
    case "musar": return "Mussar";
    case "jewish thought": return "Philosophy";
    case "philosophy": return "Philosophy";
    default: return "Other";
  }
}

export interface PassageOpts {
  displayTitle?: string;
  heDisplayTitle?: string | null;
  sefariaCategory?: string;
  text?: { he?: string; en?: string };
}

// Build the Passage record for a canonical Sefaria ref. Returns null when the
// ref has no parseable address (fail loud upstream; never guess an address).
export function passageFromRef(ref: string, opts: PassageOpts = {}): Passage | null {
  const parsed = parseCanonicalRef(ref);
  if (!parsed) return null;
  const work = workFromRef(ref, opts.sefariaCategory);
  if (!work) return null;

  // The structural base passage of "Rashi on Numbers 13:16" is "Numbers 13:16":
  // same tail, base work title - only when the tail depth fits the base text.
  let basePassageId: string | undefined;
  if (parsed.baseWorkTitle) {
    const baseSections = parsed.sections.filter((s) => typeof s === "number").slice(0, 2);
    const dafTail = typeof parsed.sections[1] === "string" ? `${parsed.sections[0]}${parsed.sections[1]}` : null;
    const tail = dafTail ?? baseSections.join(":");
    if (tail) basePassageId = passageId(`${parsed.baseWorkTitle} ${tail}`);
  }

  // Hebrew ref only when honestly derivable (known work, plain numeric tail).
  const known = lookupWork(parsed.workTitle);
  const heRef =
    known && !parsed.isRange && parsed.sections.every((s) => typeof s === "number")
      ? `${known.he} ${(parsed.sections as number[]).map(hebrewNumeral).join(":")}`
      : undefined;

  const normalizedRef: NormalizedRef = {
    originalInput: ref,
    canonicalRef: ref,
    heRef,
    workId: work.id,
    passageId: passageId(ref),
    sections: parsed.sections,
    toSections: parsed.toSections,
    isRange: parsed.isRange,
    normalizedBy: "provider", // the ref came back from a real Sefaria response
    confidence: "verified",
  };

  const hasText = !!(opts.text?.he || opts.text?.en);
  return {
    id: passageId(ref),
    workId: work.id,
    canonicalRef: ref,
    heRef,
    normalizedRef,
    refType: parsed.refType,
    address: parsed.address,
    displayTitle: opts.displayTitle ?? ref,
    heDisplayTitle: opts.heDisplayTitle ?? undefined,
    text: opts.text,
    textAvailable: hasText,
    basePassageId,
    status: hasText ? "verified" : "valid_ref_missing_text",
    providerRefs: [{ provider: "Sefaria", ref, url: sefariaUrl(ref) }],
  };
}

// ---------------------------------------------------------------------------
// Edge + Evidence construction from the navigator's honest buckets
// ---------------------------------------------------------------------------

export interface AdaptedConnection {
  edge: SourceEdge;
  evidence: Evidence[];
}

// The provider_link evidence every bucket shares: Sefaria's own link record.
function providerLinkEvidence(eid: string, item: LinkLike): Evidence {
  return {
    id: `${eid}#provider`,
    edgeId: eid,
    evidenceType: "provider_link",
    provider: "Sefaria",
    sourceUrl: sefariaUrl(item.ref),
    explanation: `Sefaria's link index records "${item.ref}" as connected (category: ${item.category}${item.linkType ? `, type: ${item.linkType}` : ""}).`,
  };
}

// Build a SourceEdge with the constant scaffolding filled in - ids/passageIds
// derived from the refs, the Sefaria provenance envelope, public visibility - so
// each bucket below states ONLY the fields that genuinely vary between relationship
// types. providerRef is the ref whose Sefaria URL backs the provenance (always the
// linked item, regardless of edge direction). A future edge-shape change is one edit
// here instead of four.
function makeEdge(
  id: string,
  fromRef: string,
  toRef: string,
  providerRef: string,
  spec: {
    relationshipType: SourceEdge["relationshipType"];
    direction: SourceEdge["direction"];
    edgeClass: SourceEdge["edgeClass"];
    strength: SourceEdge["strength"];
    confidence: SourceEdge["confidence"];
    evidenceIds: string[];
    origin: SourceEdge["provenance"]["origin"];
    algorithm?: { name: string; version: string };
  },
): SourceEdge {
  const provenance: SourceEdge["provenance"] = {
    origin: spec.origin,
    provider: "Sefaria",
    providerUrl: sefariaUrl(providerRef),
  };
  if (spec.algorithm) provenance.algorithm = spec.algorithm;
  return {
    id,
    fromPassageId: passageId(fromRef),
    toPassageId: passageId(toRef),
    fromRef,
    toRef,
    relationshipType: spec.relationshipType,
    direction: spec.direction,
    edgeClass: spec.edgeClass,
    strength: spec.strength,
    confidence: spec.confidence,
    evidenceIds: spec.evidenceIds,
    provenance,
    visibility: "public",
  };
}

// Convert one bucketed connection (current source <-> item) into a typed,
// evidence-backed edge. `currentRef` is the OPEN source the buckets hang off.
export function edgeFromBucket(
  bucket: ConnectionBucket,
  currentRef: string,
  item: LinkLike,
): AdaptedConnection {
  switch (bucket) {
    case "commentaryOn": {
      // The ref shape is structural PROOF only when the item's base IS the
      // current ref ("Tosafot on Menachot 43b" under "Menachot 43b"). An
      // on-shaped ref whose base is something ELSE ("Siftei Chakhamim on
      // Numbers 15:38" linked under "Rashi on Numbers 15:38") is NOT proven
      // to comment on the current source by its shape - asserting that would
      // invent a relationship. Such links degrade honestly:
      //   - Sefaria typed the link "commentary" -> comments_on, but only
      //     provider-typed confidence (high), provider_link evidence only.
      //   - anything else -> the neutral cross_reference shape.
      if (!structuralBaseMatches(item.ref, currentRef)) {
        if (item.linkType.trim().toLowerCase() === "commentary") {
          const eid = edgeId(bucket, item.ref, currentRef);
          const provider = providerLinkEvidence(eid, item);
          return {
            edge: makeEdge(eid, item.ref, currentRef, item.ref, {
              relationshipType: "comments_on",
              direction: "reverse_available",
              edgeClass: "commentary",
              strength: 4,
              confidence: "high", // Sefaria's typed claim, not shape-proven
              evidenceIds: [provider.id],
              origin: "provider_import",
            }),
            evidence: [provider],
          };
        }
        return edgeFromBucket("otherConnected", currentRef, item);
      }
      const eid = edgeId(bucket, item.ref, currentRef);
      const structural: Evidence = {
        id: `${eid}#structural`,
        edgeId: eid,
        evidenceType: "structural_commentary_link",
        provider: "Sefaria",
        parserName: "ref-shape",
        parserVersion: "1",
        explanation: `"${item.ref}" is structurally a commentary on "${currentRef}".`,
      };
      const provider = providerLinkEvidence(eid, item);
      return {
        edge: makeEdge(eid, item.ref, currentRef, item.ref, {
          relationshipType: "comments_on",
          direction: "reverse_available",
          edgeClass: "structural",
          strength: 5,
          confidence: "verified",
          evidenceIds: [structural.id, provider.id],
          origin: "provider_import",
        }),
        evidence: [structural, provider],
      };
    }
    case "cited": {
      // The dual gate (citations.ts): Sefaria links it AND the source's own
      // Hebrew names its title + location. Deterministic parser, not AI - but
      // not an exact quote either, so "high", not "verified".
      const eid = edgeId(bucket, currentRef, item.ref);
      const explicit: Evidence = {
        id: `${eid}#explicit`,
        edgeId: eid,
        evidenceType: "explicit_ref",
        sourcePassageId: passageId(currentRef),
        parserName: "citedInText",
        parserVersion: "1",
        provider: "Internal",
        explanation: `The source's own Hebrew text names "${item.commentatorHe ?? item.commentator}" together with this location, and Sefaria links the ref.`,
      };
      const provider = providerLinkEvidence(eid, item);
      return {
        edge: makeEdge(eid, currentRef, item.ref, item.ref, {
          relationshipType: "cites",
          direction: "one_way",
          edgeClass: "citation",
          strength: 4,
          confidence: "high",
          evidenceIds: [explicit.id, provider.id],
          origin: "algorithm_detected",
          algorithm: { name: "citedInText", version: "1" },
        }),
        evidence: [explicit, provider],
      };
    }
    case "otherConnected": {
      // A real provider link whose DIRECTION we cannot honestly assert
      // (direction-honesty rule) - so the relationship is the neutral,
      // symmetric cross_reference, backed by the provider record only.
      const eid = edgeId(bucket, currentRef, item.ref);
      const provider = providerLinkEvidence(eid, item);
      return {
        edge: makeEdge(eid, currentRef, item.ref, item.ref, {
          relationshipType: "cross_reference",
          direction: "bidirectional",
          edgeClass: "editorial",
          strength: 2,
          confidence: "high",
          evidenceIds: [provider.id],
          origin: "provider_import",
        }),
        evidence: [provider],
      };
    }
  }
}

// ---------------------------------------------------------------------------
// Journey projection (scaffold): map the live DOM-trail projection (PathNode[])
// into JourneyNode[]. The DOM path REMAINS the single source of truth
// (navigator.ts contract); this is a read-only export shape for future
// SavedJourney / journey-replay features. `openedAt` is caller-supplied, the
// same no-internal-clock convention as journey.ts.
// ---------------------------------------------------------------------------

export function journeyNodesFromTrail(
  anchorRef: string,
  nodes: PathNode[],
  openedAt: string,
): JourneyNode[] {
  const out: JourneyNode[] = [
    {
      id: "j:0",
      passageId: passageId(anchorRef),
      canonicalRef: anchorRef,
      journeyRole: "start",
      openedAt,
      depth: 0,
      isCurrent: nodes.length === 0,
    },
  ];
  nodes.forEach((n, i) => {
    out.push({
      id: `j:${i + 1}`,
      passageId: passageId(n.ref),
      canonicalRef: n.ref,
      parentJourneyNodeId: `j:${i}`,
      journeyRole: "main_path",
      openedAt,
      depth: i + 1,
      isCurrent: i === nodes.length - 1,
    });
  });
  return out;
}
